import i18n from '@/utils/i18n';
import {
  ActivityIndicator,
  GestureResponderEvent,
  Pressable,
  View,
} from 'react-native';
import { Avatar, createAvatarProps } from '@usepepper/ui/atoms/avatar';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { MiddleTruncatedTypography } from '@/experiences/sales-portal/components/middle-truncated-typography';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { RestaurantOptionSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/restaurant-option-skeleton';
import { RestaurantSelectorSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/restaurant-selector-skeleton';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import {
  SearchableDropdownMenu,
  SearchableDropdownMenuRef,
} from '@usepepper/ui/molecules/searchable-dropdown-menu';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useHover } from 'react-native-web-hooks';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

const RESTAURANT_ANCHOR_WIDTH = 284;
const RESTAURANT_DROPDOWN_WIDTH = 293;
const RESTAURANT_SELECTOR_MENU_ITEM_HEIGHT = 62;
const RESTAURANT_SELECTOR_SKELETON_ROW_COUNT = 5;

type RestaurantOptionData = RestaurantListData;

interface RestaurantOptionProps {
  isFocused?: boolean;
  isSelected: boolean;
  onSelect: () => void;
  renderRightComponent?: (isHoveredOrFocused: boolean) => React.ReactNode;
  restaurantAddress: string;
  restaurantName: string;
  restaurantUUID: string;
}

const RestaurantOption: React.FC<RestaurantOptionProps> = ({
  onSelect,
  renderRightComponent,
  restaurantAddress,
  restaurantName,
  restaurantUUID,
  isFocused = false,
  isSelected = false,
}) => {
  const [styles, theme] = useStyles();
  const avatarProps = createAvatarProps(restaurantName, restaurantUUID);

  const restaurantOptionRef =
    useRef<React.ComponentRef<typeof Pressable>>(null);
  const isHovered = useHover(restaurantOptionRef);

  const RestaurantName = (
    <MiddleTruncatedTypography
      bold
      color={theme.sk.color.text.primary}
      size="$sm"
      text={restaurantName}
      variant="body"
    />
  );

  const RestaurantAvatar = <Avatar {...avatarProps} size="small" />;

  const RestaurantAddress = (
    <Typography
      color={theme.sk.color.text.secondary}
      numberOfLines={1}
      variant="paragraph-sm"
    >
      {restaurantAddress}
    </Typography>
  );

  // Calling focus() synchronously causes the browser to scroll the element
  // into view, which fights the FlatList's scrollToIndex animation.
  // Deferring to the next animation frame lets the scroll settle first.
  useEffect(() => {
    if (!isFocused || !restaurantOptionRef.current) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      restaurantOptionRef.current?.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [isFocused]);

  // Pressing Enter on a focused Pressable synthesizes a click that bubbles
  // to the Dropdown overlay and closes it prematurely. Stop propagation so
  // the selection handler controls when the dropdown closes.
  const handlePress = useCallback(
    (e: GestureResponderEvent) => {
      e.stopPropagation();
      onSelect();
    },
    [onSelect],
  );

  const rightElement = renderRightComponent
    ? renderRightComponent(isHovered || isFocused)
    : null;

  return (
    <Pressable
      onPress={handlePress}
      ref={restaurantOptionRef}
      style={[
        styles.restaurantMenuItemContainer,
        {
          backgroundColor:
            isHovered || isSelected || isFocused
              ? theme.sk.color.bg.primary.hover
              : theme.sk.color.bg.primary.default,
        },
      ]}
    >
      <ListItem
        key={restaurantUUID}
        leftElement={RestaurantAvatar}
        rightElement={rightElement}
        style={styles.restaurantMenuItem}
        subtitle={RestaurantAddress}
        title={RestaurantName}
      />
    </Pressable>
  );
};
const MemoizedRestaurantOption = memo(RestaurantOption);

interface RestaurantSelectorProps {
  hasError: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  isOpen: boolean;
  listData: RestaurantOptionData[];
  onClear?: () => void;
  onClose?: () => void;
  onEndReached?: () => void;
  onOpen: () => void;
  onRetry: () => void;
  onSearch?: (searchText: string) => void;
  onSelectItem: (item: RestaurantOptionData) => void;
  renderItemRightComponent?: (
    item: RestaurantOptionData,
    isHoveredOrFocused: boolean,
  ) => React.ReactNode;
  selectedItem?: RestaurantOptionData | null;
}

export const RestaurantSelector: React.FC<RestaurantSelectorProps> = ({
  hasError,
  isFetchingMore,
  isLoading,
  isOpen,
  selectedItem,
  listData,
  onClear,
  onClose,
  onEndReached,
  onOpen,
  onRetry,
  onSearch,
  onSelectItem,
  renderItemRightComponent,
}) => {
  const [styles, theme] = useStyles();
  const avatarProps = createAvatarProps(
    selectedItem?.restaurantName ?? '',
    selectedItem?.key,
  );

  const [searchText, setSearchText] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const dropdownRef = useRef<SearchableDropdownMenuRef>(null);

  const handleClose = useCallback(() => {
    setSearchText('');
    setFocusedIndex(null);
    // clear search so we re-open the restaurant selector to the base list
    onClear?.();
    onClose?.();
  }, [onClear, onClose]);

  // Reset search state when dropdown closes, auto-focus search when it opens
  useEffect(() => {
    if (isOpen) {
      dropdownRef.current?.focusSearchInput();
    } else {
      setSearchText('');
      setFocusedIndex(null);
    }
  }, [isOpen]);

  const handleSearch = (text: string) => {
    setSearchText(text);

    if (text) {
      onSearch?.(text);
    } else {
      onClear?.();
    }
  };

  const handleSelectItem = useCallback(
    (item: RestaurantOptionData) => {
      handleClose();
      if (item.key !== selectedItem?.key) {
        onSelectItem(item);
      }
    },
    [handleClose, onSelectItem, selectedItem?.key],
  );

  // Keyboard shortcut: / to focus search input
  const handleSearchShortcut = useCallback(() => {
    dropdownRef.current?.focusSearchInput();
  }, []);

  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    handleSearchShortcut,
    [handleSearchShortcut],
    {},
    isOpen,
    SalesPortalKeyboardScope.LOCATION_PICKER,
  );

  // Keyboard shortcut: ↑ to move focus up in restaurant list
  const handleArrowUp = useCallback(() => {
    if (listData.length === 0) {
      return;
    }

    if (focusedIndex === null || focusedIndex === 0) {
      setFocusedIndex(null);
      dropdownRef.current?.focusSearchInput();
      return;
    }

    const newIndex = Math.max(0, focusedIndex - 1);
    setFocusedIndex(newIndex);
    dropdownRef.current?.scrollToIndex(newIndex);
  }, [listData.length, focusedIndex]);

  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.ArrowUp,
    handleArrowUp,
    [handleArrowUp],
    {},
    isOpen,
    SalesPortalKeyboardScope.LOCATION_PICKER,
  );

  // Keyboard shortcut: ↓ to move focus down in restaurant list
  const handleArrowDown = useCallback(() => {
    if (listData.length === 0) {
      return;
    }

    const newIndex = Math.min(
      listData.length - 1,
      focusedIndex === null ? 0 : focusedIndex + 1,
    );
    setFocusedIndex(newIndex);
    dropdownRef.current?.scrollToIndex(newIndex);
  }, [listData.length, focusedIndex]);

  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.ArrowDown,
    handleArrowDown,
    [handleArrowDown],
    {},
    isOpen,
    SalesPortalKeyboardScope.LOCATION_PICKER,
  );

  // Keyboard shortcut: Enter to select focused restaurant
  const handleEnter = useCallback(() => {
    if (focusedIndex !== null) {
      const focusedItem = listData[focusedIndex];
      if (focusedItem) {
        handleSelectItem(focusedItem);
      }
    }
  }, [listData, focusedIndex, handleSelectItem]);

  useScreenScopedKeyboardShortcuts(
    [Key.Enter],
    handleEnter,
    [handleEnter],
    {},
    isOpen,
    SalesPortalKeyboardScope.LOCATION_PICKER,
  );

  const RestaurantSelectorTrigger = selectedItem ? (
    <DropdownTrigger
      isOpen={isOpen}
      onPress={onOpen}
      style={styles.restaurantSelector}
    >
      <View style={styles.selectedRestaurant}>
        <Avatar {...avatarProps} size="small" />
        <MiddleTruncatedTypography
          color={theme.sk.color.text.primary}
          size="$sm"
          text={selectedItem.restaurantName}
          variant="body"
        />
      </View>
    </DropdownTrigger>
  ) : (
    <RestaurantSelectorSkeleton />
  );

  const DropdownStateComponent = hasError ? (
    <View style={styles.dropdownStateContainer}>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-md">
        {i18n.t('sales_portal.restaurant_selector.error.description')}
      </Typography>
      <Button
        accessibilityLabel={i18n.t('sales_portal.error.retry')}
        onPress={onRetry}
        size={ButtonSize.Small}
        variant={ButtonVariant.Secondary}
      >
        {i18n.t('sales_portal.error.retry')}
      </Button>
    </View>
  ) : isLoading ? (
    <View accessibilityState={{ busy: true }}>
      {Array.from({ length: RESTAURANT_SELECTOR_SKELETON_ROW_COUNT }).map(
        (_, index) => (
          <RestaurantOptionSkeleton key={index} />
        ),
      )}
    </View>
  ) : (
    <View style={styles.noRestaurantsContainer}>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-md">
        {i18n.t('sales_portal.restaurant_selector.no_results_found')}
      </Typography>
    </View>
  );

  const shouldShowLoadingMoreFooter =
    !isLoading && listData.length > 0 && !hasError && isFetchingMore;
  const LoadingMoreFooter = shouldShowLoadingMoreFooter ? (
    <View style={styles.loadingMoreFooter}>
      <ActivityIndicator color={theme.sk.color.text.secondary} size="small" />
    </View>
  ) : null;

  return (
    <SearchableDropdownMenu
      dropdownContainerOverrides={styles.dropdownContainer}
      estimatedListItemHeight={RESTAURANT_SELECTOR_MENU_ITEM_HEIGHT}
      focusedIndex={focusedIndex}
      isVisible={isOpen}
      listData={listData}
      listEmptyComponent={DropdownStateComponent}
      listFooterComponent={LoadingMoreFooter}
      onClose={handleClose}
      onEndReached={onEndReached}
      onFocusedIndexChange={setFocusedIndex}
      onSearch={handleSearch}
      placeholderText={i18n.t(
        'sales_portal.restaurant_selector.search_placeholder',
      )}
      position="bottom-start"
      ref={dropdownRef}
      renderItem={(item, index, isFocused) => (
        <MemoizedRestaurantOption
          isFocused={isFocused}
          isSelected={selectedItem?.key === item.key}
          key={item.key}
          onSelect={() => handleSelectItem(item)}
          renderRightComponent={
            renderItemRightComponent
              ? (isHoveredOrFocused) =>
                  renderItemRightComponent(item, isHoveredOrFocused)
              : undefined
          }
          restaurantAddress={item.restaurantAddress}
          restaurantName={item.restaurantName}
          restaurantUUID={item.key}
        />
      )}
      searchText={searchText}
      trigger={RestaurantSelectorTrigger}
    />
  );
};

const useStyles = createStyles((theme) => ({
  dropdownContainer: {
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    width: RESTAURANT_DROPDOWN_WIDTH,
  },
  dropdownStateContainer: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  loadingMoreFooter: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  noRestaurantsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  restaurantMenuItem: {
    alignItems: 'center',
  },
  restaurantMenuItemContainer: {
    borderRadius: theme.sk.radii.radiusSmall,
    padding: theme.sk.spacing.spaceSmall,
  },
  restaurantSelector: {
    gap: theme.sk.spacing.spaceSMedium,
    width: RESTAURANT_ANCHOR_WIDTH,
  },
  selectedRestaurant: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
